import type { ReactNode } from 'react';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Icon } from '@ui/Icon';

const useStyles = makeStyles<
  { lineGap: string; rowGap: string },
  'scopeLineButton' | 'scopeLineValue' | 'scopeLineCaret'
>()((_theme, { lineGap, rowGap }, classes) => ({
  scopeLineButton: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    width: '100%',
    minWidth: 0,
    margin: 0,
    padding: 0,
    border: 'none',
    borderRadius: 5,
    background: 'none',
    color: 'inherit',
    font: 'inherit',
    textAlign: 'left',
    cursor: 'pointer',
    opacity: 0.55,
    gap: rowGap,
    zIndex: 0,
    // Plate sits outside the text so hover does not shift the lines.
    '&::before': {
      content: '""',
      position: 'absolute',
      zIndex: -1,
      top: -3,
      right: -18,
      bottom: -3,
      left: -6,
      borderRadius: 5,
      backgroundColor: 'transparent',
    },
  },
  scopeLine: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    gap: lineGap,
  },
  scopeLineLabel: {
    flex: '0 0 auto',
  },
  scopeLineValue: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    opacity: 0.8 / 0.55,
  },
  scopeLineCaret: {
    position: 'absolute',
    top: '50%',
    right: -14,
    display: 'flex',
    alignItems: 'center',
    width: 0,
    height: 0,
    overflow: 'hidden',
    opacity: 0,
    transform: 'translateY(-50%)',
  },
  scopeOverlayAnchor: {
    position: 'relative',
    zIndex: 5,
    overflow: 'visible',
    [`&:hover .${classes.scopeLineButton}::before`]: {
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
    },
    [`&:hover .${classes.scopeLineCaret}`]: {
      width: 12,
      height: 12,
      opacity: 0.2,
    },
  },
}));

type ReportCoverScopeLine = {
  label: string;
  value: string;
};

type ReportCoverScopeLinesProps = {
  lines: readonly ReportCoverScopeLine[];
  /** App cover opens the scope overlay. Print stays static text. */
  interactive: boolean;
  lineGap: string;
  rowGap: string;
  labelMinWidth: string;
  onOpen: () => void;
  /** Scope panel. Rendered only while {@link ReportCoverScopeLinesProps.interactive} is set. */
  children?: ReactNode;
};

/**
 * LCA modules and building-parts rows on the report cover.
 * Both modes share one row list. The button, caret, and panel exist only in the app.
 */
export const ReportCoverScopeLines = ({
  lines,
  interactive,
  lineGap,
  rowGap,
  labelMinWidth,
  onOpen,
  children,
}: ReportCoverScopeLinesProps) => {
  const { classes } = useStyles({ lineGap, rowGap });
  const rows = lines.map((line) => (
    <Box
      key={line.label}
      className={interactive ? classes.scopeLine : undefined}
      sx={
        interactive
          ? undefined
          : {
              display: 'flex',
              minWidth: 0,
              gap: lineGap,
              opacity: 0.55,
            }
      }
    >
      <Box
        component="span"
        className={interactive ? classes.scopeLineLabel : undefined}
        sx={{ flex: '0 0 auto', minWidth: labelMinWidth }}
      >
        {line.label}
      </Box>
      <Box
        component="span"
        className={interactive ? classes.scopeLineValue : undefined}
        sx={
          interactive
            ? undefined
            : {
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                opacity: 0.8 / 0.55,
              }
        }
      >
        {line.value}
      </Box>
    </Box>
  ));

  if (!interactive) return <>{rows}</>;

  return (
    <Box className={classes.scopeOverlayAnchor}>
      <Box
        component="button"
        type="button"
        className={classes.scopeLineButton}
        onClick={onOpen}
      >
        {rows}
        <Box component="span" className={classes.scopeLineCaret} aria-hidden>
          <Icon category="mui" icon="KeyboardArrowDown" size={12} />
        </Box>
      </Box>
      {children}
    </Box>
  );
};
